import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, CheckCircle2 } from "lucide-react";
import PrismLogo from "@/components/PrismLogo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      // Server set the httpOnly `prismiq_session` cookie. Do NOT put the JWT into
      // JS-accessible storage — refresh() re-reads the user via /auth/me.
      await refresh();
      setDone(true);
      setTimeout(() => {
        const target =
          data.user.role === "admin" ? "/admin" : data.user.role === "teacher" ? "/teacher" : "/dashboard";
        nav(target);
      }, 1500);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="reset-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <PrismLogo size={36} />
          <span className="font-display text-xl font-extrabold text-slate-900">Prismiq</span>
        </Link>

        {!token ? (
          <div className="qs-card p-8 text-center" data-testid="reset-notoken">
            <h1 className="font-display text-2xl font-black text-slate-900">Missing token</h1>
            <p className="mt-2 text-slate-600 text-sm">Open the reset link from your email to continue.</p>
            <Link to="/forgot-password" className="btn-primary mt-6 text-sm inline-flex">
              Request a new link
            </Link>
          </div>
        ) : done ? (
          <div className="qs-card p-8 text-center" data-testid="reset-success">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl font-black text-slate-900 mt-4">Password updated</h1>
            <p className="mt-2 text-slate-600 text-sm">You&rsquo;re now signed in. Redirecting…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-black text-slate-900">Set a new password</h1>
            <p className="mt-1.5 text-slate-500 text-sm">Pick something you&rsquo;ll remember. At least 6 characters.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">New password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="At least 6 characters"
                  data-testid="reset-password"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Confirm password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="Type it again"
                  data-testid="reset-confirm"
                />
              </div>
              {err && <p className="text-sm text-rose-600" data-testid="reset-error">{err}</p>}
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-70" data-testid="reset-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
